import cx from 'clsx';
import React, { type ChangeEvent, type KeyboardEvent, useLayoutEffect, useRef, useState } from 'react';
import { type Control, type FieldError, useController } from 'react-hook-form';
import { type PlacesType, Tooltip } from 'react-tooltip';

import { ReactComponent as InfoIcon } from '@assets/svg/info.svg';

import useTranslation from '@hooks/useTranslation';

import { selectAppRtl } from '@store/useAppStore';

import style from './style.module.scss';

interface ICommonProps {
  placeholder?: string;
  label?: string;
  rightElement?: React.ReactNode;
  leftElement?: React.ReactNode;
  inputType?: string;
  disabled?: boolean;
  required?: boolean;
  dataQa?: string;
  type?: string;
  tooltipContent?: string;
  autoFocus?: boolean;
  className?: string;
  tooltipPlace?: PlacesType;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
}

interface IUncontrolledProps extends ICommonProps {
  value: string | number;
  name?: string;
  error?: FieldError | { message: string };
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: () => void;
}

interface IControlledProps extends ICommonProps {
  name: string;
  control: Control<any>;
}

const Component: React.FC<IUncontrolledProps> = ({
  value,
  error,
  name,
  placeholder,
  label,
  rightElement,
  leftElement,
  inputType = 'text',
  disabled = false,
  required = false,
  dataQa,
  type,
  tooltipContent,
  tooltipPlace = 'bottom-start',
  autoFocus = false,
  className,
  onChange,
  onBlur,
  onKeyDown,
}) => {
  const { t } = useTranslation();
  const rtl = selectAppRtl();
  const leftElementRef = useRef<HTMLDivElement | null>(null);

  const [leftElementWidth, setLeftElementWidth] = useState(0);

  useLayoutEffect(() => {
    if (leftElementRef.current) {
      setLeftElementWidth(leftElementRef.current.offsetWidth);
    }
  }, []);

  const dynamicLeftElementWidthPadding = leftElement ? { paddingLeft: 20 + leftElementWidth } : undefined;

  return (
    <div className={cx(style.inputBlock, className)}>
      {label && (
        <div className={style.inputHeader}>
          <label className={cx(style.label, { [style.required]: required })} htmlFor={name}>
            {label}
          </label>
          {tooltipContent && (
            <div id={`input-details-${name}-tooltip`} className={style.inputDetailsWrapper}>
              <InfoIcon className={style.inputDetailsIcon} />
              <Tooltip
                anchorSelect={`#input-details-${name}-tooltip`}
                content={tooltipContent}
                place={tooltipPlace}
                className={style.inputDetails}
              />
            </div>
          )}
        </div>
      )}
      <div className={style.inputWrapper}>
        {leftElement && (
          <div ref={leftElementRef} className={cx(style.extraElement, style.extraElementLeft)}>
            {leftElement}
          </div>
        )}
        <input
          className={cx(style.inputField, {
            [style.inputFieldRtl]: rtl,
            [style.leftIndent]: !!leftElement,
            [style.rightIndent]: !!rightElement,
            [style.inputFilledField]: !!value,
            [style.inputErrorField]: !!error,
          })}
          style={dynamicLeftElementWidthPadding}
          type={inputType}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          value={value}
          id={name}
          data-qa={dataQa || name}
          autoFocus={autoFocus}
          onChange={onChange}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
        />
        {rightElement && (
          <div className={cx(style.extraElement, style.extraElementRight)}>{rightElement}</div>
        )}
      </div>
      {type !== 'woHelperText' && <div className={style.helperText}>{error && t(error?.message)}</div>}
    </div>
  );
};

const Controlled: React.FC<IControlledProps> = ({
  name,
  control,
  placeholder,
  label,
  rightElement,
  leftElement,
  inputType,
  disabled,
  required,
  dataQa,
  type,
  tooltipContent,
  autoFocus,
  className,
  tooltipPlace,
  onKeyDown,
}) => {
  const {
    field: { value, onChange, onBlur },
    fieldState: { error },
  } = useController({
    control,
    name,
  });

  return (
    <Component
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      label={label}
      rightElement={rightElement}
      leftElement={leftElement}
      error={error}
      inputType={inputType}
      disabled={disabled}
      required={required}
      dataQa={dataQa}
      onBlur={onBlur}
      type={type}
      tooltipContent={tooltipContent}
      autoFocus={autoFocus}
      className={className}
      tooltipPlace={tooltipPlace}
      onKeyDown={onKeyDown}
    />
  );
};

const Input: React.FC<IControlledProps | IUncontrolledProps> = (props) => {
  if ('control' in props) {
    return <Controlled {...props} />;
  }
  return <Component {...props} />;
};

export default Input;
